import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CODES } from "../data/codebook";
import { deleteCustomCode, watchCustomCodes, type CustomCode } from "../data/custom-codes";
import { isFirebaseConfigured } from "../firebase";
import { getCurrentUid } from "../game/rooms";
import type { CodeSentence } from "../game/types";
import type { Lang } from "../i18n";

export default function Codes() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage as Lang) ?? "en";
  const [custom, setCustom] = useState<CustomCode[]>([]);
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    if (!isFirebaseConfigured()) return;
    getCurrentUid().then(setUid).catch(() => {});
    const unsub = watchCustomCodes(setCustom);
    return () => unsub();
  }, []);

  async function handleDelete(id: string) {
    if (!confirm("Delete this code?")) return;
    await deleteCustomCode(id);
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>Code library</h2>
          <p style={{ color: "var(--ink-muted)", fontSize: 13, margin: "4px 0 0" }}>
            {CODES.length} built-in · {custom.length} custom
          </p>
        </div>
        <Link to="/codes/new" className="btn primary">+ New code</Link>
      </div>

      {!isFirebaseConfigured() && (
        <div className="card" style={{ marginBottom: 16, background: "#fff7e0", borderLeft: "6px solid var(--accent)" }}>
          Adding custom codes needs Firebase configured.
        </div>
      )}

      {custom.length > 0 && (
        <>
          <h3>Custom codes</h3>
          <div className="codes-grid">
            {custom.map((c) => (
              <CodeCard
                key={c.id}
                code={c}
                lang={lang}
                authorName={c.authorName}
                editable={uid === c.authorUid}
                onDelete={() => handleDelete(c.id)}
              />
            ))}
          </div>
        </>
      )}

      <h3 style={{ marginTop: custom.length > 0 ? 24 : 0 }}>Built-in codes</h3>
      <div className="codes-grid">
        {CODES.map((c) => (
          <CodeCard key={c.id} code={c} lang={lang} />
        ))}
      </div>
    </div>
  );
}

function CodeCard({
  code,
  lang,
  authorName,
  editable,
  onDelete,
}: {
  code: CodeSentence;
  lang: Lang;
  authorName?: string;
  editable?: boolean;
  onDelete?: () => void;
}) {
  return (
    <div className="card" style={{ padding: 16, display: "grid", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span className="pill accent">#{code.id}</span>
        <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>{code.iconCount} icons</span>
      </div>
      <p style={{ margin: 0, fontStyle: "italic", color: "var(--ink-dark)" }}>
        "{code.translations[lang] ?? code.translations.en}"
      </p>
      {authorName && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--ink-muted)" }}>by {authorName}</p>
      )}
      {editable && (
        <div style={{ display: "flex", gap: 6 }}>
          <Link to={`/codes/${code.id}/edit`} className="btn ghost" style={{ padding: "4px 10px", fontSize: 12 }}>Edit</Link>
          <button className="btn ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--danger)" }} onClick={onDelete}>Delete</button>
        </div>
      )}
    </div>
  );
}
